import React from "react";
import type {NodeProps} from "@xyflow/react";
import {Handle, Position} from "@xyflow/react";
import {Box, Chip, IconButton, Tooltip, Typography, useTheme} from "@mui/material";
import {ChevronRight, DeleteOutline, ExpandMore} from "@mui/icons-material";
import type {TaxonomyCanvasNode} from "~/prototypes/pages/taxonomy-canvas/taxonomyCanvasLayout.ts";
import {NODE_HEIGHT, NODE_WIDTH} from "~/prototypes/pages/taxonomy-canvas/taxonomyCanvasLayout.ts";
import {spendBorderColor, spendColor, spendTextColor} from "~/prototypes/pages/taxonomy-canvas/spendColor.ts";
import {CurrencyComponent} from "~/components/currency-component/CurrencyComponent.tsx";
import {ClickToEdit} from "~/components/ClickToEdit/ClickToEdit.tsx";

export const TaxonomyCanvasNodeView: React.FC<NodeProps<TaxonomyCanvasNode>> = ({data}) => {
    const theme = useTheme();
    const isRoot = data.level === 0;
    const hasChildren = data.childCount > 0;
    return <Box sx={{
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        px: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderRadius: 2,
        border: 1,
        borderColor: isRoot ? 'primary.main' : spendBorderColor(theme, data.spendShare),
        bgcolor: isRoot ? 'primary.main' : spendColor(theme, data.spendShare),
        color: isRoot ? 'primary.contrastText' : spendTextColor(theme, data.spendShare),
        position: 'relative',
        boxShadow: data.dropAction ? 10 : data.dragging ? 6 : 1,
        opacity: data.dragging ? 0.9 : 1,
        // The drop target grows a bit so it stands out under the dragged card
        transform: data.dropAction ? 'scale(1.08)' : undefined,
        transition: 'transform 150ms ease-out, box-shadow 150ms ease-out',
        // Dashed ring = valid drop target while dragging; solid ring = category chosen in the filters
        outline: data.dropAction
            ? `3px dashed ${theme.palette.mithraBlue[800]}`
            : data.focused ? `3px solid ${theme.palette.mithraBlue[800]}` : undefined,
        outlineOffset: 3,
        cursor: data.dragging ? 'grabbing' : hasChildren ? 'pointer' : 'default',
        '&:hover': hasChildren ? {boxShadow: 3} : undefined,
        // Delete button only shows while hovering the card
        '&:hover .taxonomy-node-delete, & .taxonomy-node-delete:focus-visible': {opacity: 1},
    }}>
        <Handle type="target" position={Position.Left} isConnectable={false} style={{visibility: 'hidden'}}/>
        {data.dropAction && <Chip
            size="small"
            label={data.dropAction === 'merge' ? 'Merge into' : 'Move under'}
            sx={{
                position: 'absolute',
                top: -12,
                right: 8,
                height: 20,
                fontSize: 11,
                fontWeight: 600,
                bgcolor: 'mithraBlue.800',
                color: 'common.white',
            }}
        />}
        <Box sx={{flex: 1, minWidth: 0}}>
            {/* Clicking the name edits it; clicking the rest of the card expands / collapses */}
            <ClickToEdit
                text={data.label}
                onSave={data.onRename}
                confirmBeforeSave
                className="nodrag nopan"
                typographyProps={{variant: 'body2', fontWeight: 600, title: data.label}}
                inputSx={{color: 'text.primary', bgcolor: 'background.paper'}}
                // Keep the node colour on hover; the outline and edit icon show it is editable
                sx={{bgcolor: 'transparent', ml: -0.5, py: 0}}
            />
            <Typography variant="caption" component="div" sx={{opacity: 0.8}} noWrap>
                {isRoot ? 'Taxonomy' : `L${data.level}`}
                {' · '}<CurrencyComponent v={data.spend} nDigits={3}/>
                {hasChildren && ` · ${data.childCount} sub`}
            </Typography>
        </Box>
        {data.onDelete && <Tooltip title="Delete category">
            <IconButton
                className="taxonomy-node-delete nodrag nopan"
                size="small"
                aria-label={`Delete ${data.label}`}
                sx={{color: 'inherit', opacity: 0, transition: 'opacity 120ms', p: 0.25}}
                onClick={e => {
                    // Don't expand / collapse the node
                    e.stopPropagation();
                    data.onDelete?.();
                }}
            >
                <DeleteOutline fontSize="small"/>
            </IconButton>
        </Tooltip>}
        {hasChildren && (data.expanded ? <ExpandMore fontSize="small"/> : <ChevronRight fontSize="small"/>)}
        <Handle type="source" position={Position.Right} isConnectable={false} style={{visibility: 'hidden'}}/>
    </Box>
}
